import { useState, type FormEvent } from 'react';

const SUBMIT_URL = 'https://readdy.ai/api/form/db650vi2sgkaik1unc7g';
const inputCls = 'w-full h-12 rounded-md border border-background-300 bg-background-100 px-4 text-[16px] text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-foreground-950 focus:ring-2 focus:ring-foreground-950/10 transition-colors';

export default function EnquiryForm() {
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [msgLen, setMsgLen] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const trap = String(fd.get('contact_alt') || '').trim();
    if (trap) {
      setStatus('success');
      return;
    }
    fd.delete('contact_alt');
    const name = String(fd.get('name') || '').trim();
    const email = String(fd.get('email') || '').trim();
    const message = String(fd.get('message') || '').trim();
    const next: Record<string, string> = {};
    if (!name) next.name = 'Please enter your name.';
    if (!email) next.email = 'Please enter your email.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) next.email = 'Please enter a valid email address.';
    if (!message) next.message = 'Please add a short message.';
    else if (message.length > 500) next.message = 'Please keep your message under 500 characters.';
    setErrors(next);
    if (Object.keys(next).length) return;

    const params = new URLSearchParams();
    fd.forEach((v, k) => {
      if (typeof v === 'string' && v.trim()) params.append(k, v.trim());
    });

    setStatus('sending');
    setFormError('');
    try {
      const response = await fetch(SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const isSpam = /spam/i.test(serverMsg);
      if (response.ok && parsed?.code === 'OK' && !isSpam) {
        setStatus('success');
        form.reset();
        setMsgLen(0);
      } else {
        setStatus('error');
        setFormError(serverMsg || 'Something went wrong sending your enquiry. Please try again.');
      }
    } catch {
      setStatus('error');
      setFormError('We couldn\u2019t reach the server. Please check your connection and try again.');
    }
  };

  const fieldErr = (k: string) => errors[k] && <p id={`${k}-err`} className="mt-2 text-[13px] text-secondary-500">{errors[k]}</p>;

  return (
    <form id="visit-enquiry" data-readdy-form noValidate onSubmit={handleSubmit} className="relative grid grid-cols-1 sm:grid-cols-2 gap-5">
      <div>
        <label htmlFor="f-name" className="block text-[14px] font-medium mb-2">Name *</label>
        <input id="f-name" name="name" type="text" autoComplete="name" aria-invalid={!!errors.name} aria-describedby={errors.name ? 'name-err' : undefined} className={inputCls} />
        {fieldErr('name')}
      </div>
      <div>
        <label htmlFor="f-email" className="block text-[14px] font-medium mb-2">Email *</label>
        <input id="f-email" name="email" type="email" autoComplete="email" aria-invalid={!!errors.email} aria-describedby={errors.email ? 'email-err' : undefined} className={inputCls} />
        {fieldErr('email')}
      </div>
      <div>
        <label htmlFor="f-phone" className="block text-[14px] font-medium mb-2">Phone <span className="text-foreground-500 font-normal">(optional)</span></label>
        <input id="f-phone" name="phone" type="tel" autoComplete="tel" className={inputCls} />
      </div>
      <div>
        <label htmlFor="f-interest" className="block text-[14px] font-medium mb-2">Interested In <span className="text-foreground-500 font-normal">(optional)</span></label>
        <div className="relative">
          <select id="f-interest" name="interested_in" defaultValue="" className={`${inputCls} appearance-none pr-10 cursor-pointer`}>
            <option value="">Select an option</option>
            <option>Residential Living &amp; Support</option>
            <option>Personalised Daily Care</option>
            <option>Memory &amp; Wellbeing</option>
            <option>Short-Stay &amp; Respite Care</option>
            <option>Not sure yet</option>
          </select>
          <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center"><i className="ri-arrow-down-s-line" /></span>
        </div>
      </div>
      <div className="sm:col-span-2">
        <label htmlFor="f-message" className="block text-[14px] font-medium mb-2">Message *</label>
        <textarea
          id="f-message"
          name="message"
          rows={5}
          maxLength={500}
          onChange={(e) => setMsgLen(e.target.value.length)}
          aria-invalid={!!errors.message}
          aria-describedby={errors.message ? 'message-err' : undefined}
          className={`${inputCls} h-auto py-3 resize-none`}
          placeholder="Tell us a little about who you're enquiring for and what matters most."
        />
        <div className="flex justify-between">
          {fieldErr('message') || <span />}
          <span className="mt-2 text-[12px] text-foreground-500">{msgLen}/500</span>
        </div>
      </div>
      <input type="text" name="contact_alt" className="form-extra-field" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly onFocus={(e) => e.currentTarget.removeAttribute('readonly')} />
      <div className="sm:col-span-2 flex flex-col sm:flex-row sm:items-center gap-4">
        <button type="submit" disabled={status === 'sending'} className="h-12 px-8 rounded-full bg-primary-500 text-background-50 text-[15px] font-medium whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors duration-200 disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500">
          {status === 'sending' ? 'Sending\u2026' : 'Send Enquiry'}
        </button>
        <div aria-live="polite" className="text-[14px]">
          {status === 'success' && <p className="text-foreground-950">Thank you &mdash; your enquiry has been received. We&rsquo;ll be in touch soon.</p>}
          {status === 'error' && <p className="text-secondary-500">{formError}</p>}
        </div>
      </div>
    </form>
  );
}